import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "input",
    "dropzone",
    "prompt",
    "previewContainer",
    "previewImage",
    "plate",
    "plateName",
    "meta",
    "fileName",
    "fileSize",
    "fileActions",
    "errorMessage",
    "submit",
    "caption"
  ]

  static values = {
    maxSize: { type: Number, default: 10 * 1024 * 1024 },
    allowedExtensions: { type: Array, default: ["jpeg", "jpg", "heic"] },
    invalidType: { type: String, default: "JPEG or HEIC only." },
    tooLarge: { type: String, default: "10 MB is the limit." },
    missing: { type: String, default: "Choose a photograph first." },
    submitting: { type: String, default: "Submitting..." }
  }

  connect() {
    this.onSubmit = this.onFormSubmit.bind(this)
    this.element.addEventListener("submit", this.onSubmit)
  }

  disconnect() {
    this.element.removeEventListener("submit", this.onSubmit)
  }

  handleDragEnter(event) {
    event.preventDefault()
    this.markHot(event.currentTarget, true)
  }

  handleDragLeave(event) {
    event.preventDefault()
    this.markHot(event.currentTarget, false)
  }

  handleDragOver(event) {
    event.preventDefault()
  }

  handleDrop(event) {
    event.preventDefault()
    this.markHot(event.currentTarget, false)
    const file = event.dataTransfer?.files?.[0]
    if (file) this.validateAndHandleFile(file)
  }

  handleInputChange(event) {
    const file = event.target.files?.[0]
    if (file) this.validateAndHandleFile(file)
  }

  triggerInput(event) {
    if (event?.target === this.inputTarget) return
    this.inputTarget.click()
  }

  removeFile() {
    this.inputTarget.value = ""
    this.previewImageTarget.removeAttribute("src")
    this.previewImageTarget.hidden = true
    this.plateTarget.hidden = true
    if (this.hasPromptTarget) this.promptTarget.hidden = false
    if (this.hasPreviewContainerTarget) this.previewContainerTarget.hidden = true
    if (this.hasMetaTarget) this.metaTarget.hidden = true
    if (this.hasFileActionsTarget) this.fileActionsTarget.hidden = true
    this.clearErrorMessage()
  }

  validateAndHandleFile(file) {
    const extension = this.getFileExtension(file.name)
    if (!this.allowedExtensionsValue.includes(extension.toLowerCase())) {
      this.showErrorMessage(this.invalidTypeValue)
      return
    }

    if (file.size > this.maxSizeValue) {
      this.showErrorMessage(this.tooLargeValue)
      return
    }

    const dataTransfer = new DataTransfer()
    dataTransfer.items.add(file)
    this.inputTarget.files = dataTransfer.files
    this.clearErrorMessage()
    this.updatePreview(file)
  }

  updatePreview(file) {
    const extension = this.getFileExtension(file.name).toUpperCase()
    this.fileNameTarget.textContent = file.name
    this.fileSizeTarget.textContent = `${this.formatSize(file.size)} · ${extension}`

    if (extension === "HEIC") {
      this.previewImageTarget.hidden = true
      this.previewImageTarget.removeAttribute("src")
      this.plateNameTarget.textContent = file.name
      this.plateTarget.hidden = false
    } else {
      const reader = new FileReader()
      reader.onloadend = () => {
        this.previewImageTarget.src = reader.result
        this.previewImageTarget.hidden = false
        this.plateTarget.hidden = true
      }
      reader.readAsDataURL(file)
    }

    if (this.hasPromptTarget) this.promptTarget.hidden = true
    if (this.hasPreviewContainerTarget) this.previewContainerTarget.hidden = false
    if (this.hasMetaTarget) this.metaTarget.hidden = false
    if (this.hasFileActionsTarget) this.fileActionsTarget.hidden = false
    if (this.hasCaptionTarget) this.captionTarget.focus()
  }

  onFormSubmit(event) {
    const file = this.inputTarget.files?.[0]
    if (!file) {
      event.preventDefault()
      this.showErrorMessage(this.missingValue)
      return
    }

    const extension = this.getFileExtension(file.name)
    if (!this.allowedExtensionsValue.includes(extension.toLowerCase())) {
      event.preventDefault()
      this.showErrorMessage(this.invalidTypeValue)
      return
    }

    if (file.size > this.maxSizeValue) {
      event.preventDefault()
      this.showErrorMessage(this.tooLargeValue)
      return
    }

    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = true
      this.submitTarget.value = this.submittingValue
    }
  }

  markHot(el, on) {
    if (!this.hasDropzoneTarget || el !== this.dropzoneTarget) return
    el.classList.toggle("ring-2", on)
    el.classList.toggle("ring-inset", on)
    el.classList.toggle("ring-white", on)
  }

  formatSize(bytes) {
    if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  getFileExtension(filename) {
    return filename.split(".").pop() || ""
  }

  showErrorMessage(message) {
    if (!this.hasErrorMessageTarget) return
    this.errorMessageTarget.textContent = message
    this.errorMessageTarget.classList.remove("hidden")
  }

  clearErrorMessage() {
    if (!this.hasErrorMessageTarget) return
    this.errorMessageTarget.textContent = ""
    this.errorMessageTarget.classList.add("hidden")
  }
}
